import { ChangeEvent, useState } from 'react'
import { To } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { useDocumentsDataTable } from 'generated/resources/datatable.graphql'
import { DataTableFeatureFlagDocument } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import ActionsDatatable from 'modules/documents/components/ActionsDatatable'
import BulkActionsDatatable from 'modules/documents/components/BulkActionsDatatable'
import ContextFromPage from 'modules/documents/components/DatatableContextFromPage/ContextFromPage'
import DocumentsDataTableEmptyState from 'modules/documents/components/DocumentDatatableEmptyState'
import {
  createdAtFilter,
  signatureStatusFilter,
} from 'modules/documents/components/DocumentsDatatable/filters'
import DraggableDatatable from 'modules/documents/components/DraggableDatatable'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { SPACES } from 'modules/documents/lib/constants'
import {
  actionsProjection,
  authorProjection,
  contentTypeProjection,
  createdAtProjection,
  filenameProjection,
  signatureStatusProjection,
} from 'modules/documents/lib/datatable/documents/projections'

const ITEMS_PER_PAGE = 100

type Props = {
  onNavigationClick: (documentId: string) => void
  folder?: Folder | null
  initialFolder?: Folder | null
  employeeId?: string
  parentRoute?: To
  onDrop: (files: File[]) => void
  multiple?: boolean
  readonly: boolean
  onUpload?: () => void
  isLoading?: boolean
}

const DocumentsDataTable = ({
  folder = null,
  initialFolder,
  employeeId,
  parentRoute,
  onDrop,
  multiple = true,
  readonly,
  onNavigationClick,
  onUpload,
  isLoading = false,
}: Props) => {
  const session = useCurrentSession()
  const companyId = session.company?.id
  const { data: featureData } = useQuery(DataTableFeatureFlagDocument, {
    variables: { companyId: companyId ?? 0 },
    skip: companyId == null,
  })
  const deleteFeatureEnabled =
    featureData?.features?.featuresConnection?.nodes?.[0]?.enabled ?? false

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const [isFiltering, setIsFiltering] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const source = useDocumentsDataTable(
    [
      contentTypeProjection(),
      filenameProjection(),
      authorProjection(),
      createdAtProjection(),
      signatureStatusProjection(),
      actionsProjection({
        render: (document) => (
          <ActionsDatatable
            document={document}
            onNavigationClick={onNavigationClick}
            space={SPACES.EMPLOYEE_MY_DOCUMENTS}
            deleteFeatureEnabled={deleteFeatureEnabled}
          />
        ),
      }),
    ],
    {
      variables: {
        space: SPACES.EMPLOYEE_MY_DOCUMENTS,
        search: debouncedSearch,
        folderId: folder?.id,
        byWithoutFolder: folder === null,
        employeeId: employeeId,
      },
    }
  )

  const onDatatableSearch = (value: ChangeEvent<HTMLInputElement>) => {
    onSearch(value)

    if (value.target.value !== '') setIsFiltering(true)
    else {
      setTimeout(() => {
        setIsFiltering(false)
      }, 500)
    }
  }
  const onDragging = (value: boolean) => {
    setIsDragging(value)
  }

  const filters = [createdAtFilter(source), signatureStatusFilter(source)]
  const datatable = (
    <DataTable.Provider
      source={source}
      filters={filters}
      enableClearFilters
      pagination={{
        kind: 'offset',
        itemsPerPage: ITEMS_PER_PAGE,
      }}
      sort={{
        path: 'createdAt',
        direction: 'desc',
      }}
    >
      <Box paddingY="s8" gap="s16">
        <DataTable.HeadBar
          search={{
            value: search,
            onChange: onDatatableSearch,
            placeholder: i18n.t('common.search'),
          }}
          actions={
            !readonly && (
              <BulkActionsDatatable
                space={SPACES.EMPLOYEE_MY_DOCUMENTS}
                deleteFeatureEnabled={deleteFeatureEnabled}
              />
            )
          }
          disableDefaultActions
        />
      </Box>
      <DataTable.Table
        selectable={() => true}
        emptyState={
          <DocumentsDataTableEmptyState
            onUpload={readonly ? undefined : onUpload}
            withFilters={isFiltering}
            isDragging={isDragging}
          />
        }
      />
      <ContextFromPage
        parentRoute={parentRoute}
        folder={folder}
        employeeId={employeeId}
        initialFolder={initialFolder}
        isLoading={isLoading}
      />
    </DataTable.Provider>
  )

  if (readonly) {
    return datatable
  }

  return (
    <DraggableDatatable onDrop={onDrop} multiple={multiple} onDragging={onDragging}>
      {datatable}
    </DraggableDatatable>
  )
}

export default DocumentsDataTable
